/**
 * Backup / restore service — SuperUser-only Admin API endpoints.
 *
 * The schedule read/write goes through the shared `httpClient` (Bearer +
 * envelope unwrap). The two binary-ish operations need special handling that
 * `httpClient` (which reads the body as text/JSON) can't do, so they build
 * their own `fetch`:
 *   - the manual backup DOWNLOAD returns a binary `.rmtkbk` stream that must be
 *     read as a Blob and saved to disk;
 *   - both backup and restore require the `X-Backup-Passphrase` header.
 *
 * Auth is reused from `httpClient.resolveAuthToken` (preflights the SSO token
 * refresh so the Bearer on the wire is valid for at least 30 s).
 */

import { httpClient, resolveAuthToken } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/constants';
import type { NotifyEnvelope } from '@/types/api';
import type { BackupSchedule, BackupScheduleUpdate } from '../types/backup';

export const PASSPHRASE_HEADER = 'X-Backup-Passphrase';
export const MIN_PASSPHRASE = 8;

export const getSchedule = async (): Promise<BackupSchedule> => {
  const { data } = await httpClient.get<NotifyEnvelope<BackupSchedule>>(
    '/admin/backup/schedule',
    { skipGlobalLoader: true },
  );
  return data.Response;
};

export const saveSchedule = async (
  body: BackupScheduleUpdate,
): Promise<BackupSchedule> => {
  const { data } = await httpClient.put<NotifyEnvelope<BackupSchedule>>(
    '/admin/backup/schedule',
    body,
  );
  return data.Response;
};

/**
 * Derives a filename from a Content-Disposition header, falling back to
 * `backup-<YYYY-MM-DD>.rmtkbk`.
 */
const filenameFrom = (disposition: string | null): string => {
  if (disposition) {
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8?.[1]) return decodeURIComponent(utf8[1]);
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    if (plain?.[1]) return plain[1];
  }
  const date = new Date().toISOString().slice(0, 10);
  return `backup-${date}.rmtkbk`;
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * GET /admin/backup — downloads the encrypted `.rmtkbk` archive. The response
 * is a raw binary stream (NOT the Notify envelope), so it's read as a Blob and
 * saved to disk. On error the server may reply with an envelope/text body,
 * which is surfaced as the thrown message.
 */
export const downloadBackup = async (passphrase: string): Promise<void> => {
  const token = await resolveAuthToken();
  const response = await fetch(`${API_CONFIG.BASE_URL}/admin/backup`, {
    method: 'GET',
    headers: {
      accept: '*/*',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      [PASSPHRASE_HEADER]: passphrase,
    },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(detail || response.statusText);
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filenameFrom(response.headers.get('Content-Disposition')));
};

/**
 * POST /admin/restore — multipart upload of a `.rmtkbk` archive plus the
 * decryption passphrase header. DESTRUCTIVE: replaces the current database.
 * Uses the shared httpClient (FormData bypasses JSON serialisation) with the
 * passphrase added as a custom header.
 */
export const restoreBackup = async (
  file: File,
  passphrase: string,
): Promise<void> => {
  const body = new FormData();
  body.append('file', file);
  await httpClient.post<NotifyEnvelope<{ restored: boolean }>>(
    '/admin/restore',
    body,
    { headers: { [PASSPHRASE_HEADER]: passphrase } },
  );
};
